import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import type { CoordinateSystemConfig, ReactionGroup, WidgetControl } from '@examdojo/core/widget';
import type { GeometryPadDisplayConfig, GeometryPadParams, GeometryPadWidgetConfig } from '../geometrypad.model';
import { GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS } from '../geometrypad.model';
import { computeAutoBoundingBox } from '../placement';
import { ElementListSectionComponent } from './sections/element-list-section.component';

@Component({
  selector: 'dojo-geometrypad-editor',
  standalone: true,
  imports: [ElementListSectionComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './geometrypad-editor.component.html',
  styleUrl: './geometrypad-editor.component.scss',
  host: { class: 'block h-full' },
})
export class GeometrypadEditorComponent {
  readonly config = input.required<GeometryPadWidgetConfig>();
  readonly configChange = output<GeometryPadWidgetConfig>();

  readonly params = computed(() => this.config().params);
  readonly display = computed(() => {
    const d = this.config().display;
    // Surface effective canvas defaults so the editor reflects actual widget behavior
    return {
      ...(d ?? {}),
      canvas: {
        ...(d?.canvas ?? {}),
        color: d?.canvas?.color ?? 'neutral-50',
      },
    };
  });
  readonly coordSystem = computed(() => {
    const cs = this.config().display?.coordinateSystem;
    const autoBbox = computeAutoBoundingBox(this.config().params);
    // Surface effective defaults so the editor reflects actual widget behavior
    return {
      ...GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS,
      ...(cs ?? {}),
      boundingbox: cs?.boundingbox ?? autoBbox,
      axes: cs?.axes ?? GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS.axes,
      grid: cs?.grid ?? GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS.grid,
      fixedAxis: cs?.fixedAxis ?? GEOMETRYPAD_COORDINATE_SYSTEM_DEFAULTS.fixedAxis,
    };
  });
  readonly geometryDisplay = computed(() => this.config().display?.geometry);
  readonly behaviorControls = computed(() => this.config().behavior?.controls ?? []);
  readonly interactivePorts = computed(() => this.config().behavior?.interactive ?? []);
  readonly reactions = computed(() => this.config().behavior?.reactions ?? []);

  onCoordSystemChange(coordSystem: CoordinateSystemConfig): void {
    this.emitUpdate((draft) => {
      draft.display = { ...(draft.display ?? {}), coordinateSystem: coordSystem };
    });
  }

  onParamsChange(params: GeometryPadParams): void {
    this.emitUpdate((draft) => {
      draft.params = params;
    });
  }

  onDisplayChange(display: GeometryPadDisplayConfig): void {
    this.emitUpdate((draft) => {
      draft.display = { ...(draft.display ?? {}), ...display };
    });
  }

  onBehaviorControlsChange(controls: WidgetControl[]): void {
    this.emitUpdate((draft) => {
      draft.behavior = { ...(draft.behavior ?? {}), controls };
    });
  }

  onInteractivePortsChange(interactive: string[]): void {
    this.emitUpdate((draft) => {
      draft.behavior = { ...(draft.behavior ?? {}), interactive };
    });
  }

  onReactionsChange(reactions: ReactionGroup[]): void {
    this.emitUpdate((draft) => {
      draft.behavior = { ...(draft.behavior ?? {}), reactions: reactions.length ? reactions : undefined };
    });
  }

  private emitUpdate(mutate: (draft: GeometryPadWidgetConfig) => void): void {
    const draft = structuredClone(this.config());
    mutate(draft);
    this.configChange.emit(draft);
  }
}
